import {
  ENVIRONMENT_INITIALIZER,
  EnvironmentProviders,
  Injectable,
  NgZone,
  inject,
  makeEnvironmentProviders,
  signal,
} from '@angular/core';
import { environment } from '@env/environment';

/** Rolling window for rate computation. */
const WINDOW_MS = 1000;

/** How often the UI re-reads rates while the inspector is open. */
const UI_REFRESH_MS = 500;

/** How often we sweep hot overlays to refresh labels / drop cooled-down ones. */
const SWEEP_MS = 500;

/** Per-instance throttle between hot-state evaluations during `record()`. */
const HOT_DEBOUNCE_MS = 150;

/** Minimum update rate to be eligible for "hot" — under this, we don't care. */
const MIN_UPDATES_PER_SEC = 10;

/** Hot if this instance's avg-per-update is FACTOR× the global baseline. */
const HOT_FACTOR = 3;

/** Baseline = median of the last N duration samples across instances. */
const BASELINE_SAMPLES = 200;

/**
 * Floor applied to the baseline before multiplying by FACTOR. Prevents two
 * failure modes: (a) when the app is quiet and baseline is near-zero, any
 * render looks "infinite ×" — we don't want false positives; (b) when the
 * culprit dominates the sample buffer and drags baseline up, the floor
 * keeps the effective threshold meaningful (~0.06ms avg at floor × factor).
 */
const MIN_BASELINE_MS = 0.02;

/** Overlay styling — matches the inspector's "highlighted" red overlay. */
const HOT_COLOR = '#dc2626';
const HOT_LABEL_BG = 'rgba(220, 38, 38, 0.92)';

interface InstanceStats {
  /** End-of-render timestamps (ms). */
  timestamps: number[];
  /** Parallel array: duration of each render (ms). */
  durations: number[];
}

interface HotOverlay {
  element: HTMLElement;
  origBoxShadow: string;
  origOutline: string;
  origOutlineOffset: string;
  label: HTMLElement;
}

/**
 * Detects excessive re-renders by monkey-patching each component class's
 * `ɵcmp.template` function and measuring CPU time per render. A component
 * is flagged "hot" when its average render cost is significantly above the
 * app-wide baseline (median) — machine-independent by construction.
 *
 * On-screen feedback mimics the inspector's highlight overlay, so the
 * detector works even without the inspector open. Gated by
 * `environment.production`: all operations are no-ops in prod builds.
 * `ɵcmp` is a private Angular API; safe for dev-tooling only.
 */
@Injectable({ providedIn: 'root' })
export class RenderTrackerService {
  readonly enabled = !environment.production;

  private readonly ngZone = inject(NgZone);

  private readonly _tick = signal(0);
  readonly tick = this._tick.asReadonly();

  private readonly stats = new WeakMap<object, InstanceStats>();
  private readonly lastHotCheck = new WeakMap<object, number>();
  private readonly hotOverlays = new WeakMap<object, HotOverlay>();
  private readonly hotRefs = new Set<WeakRef<object>>();
  private readonly recentAvgs: number[] = [];

  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private sweepTimer: ReturnType<typeof setInterval> | null = null;
  private mutationObserver: MutationObserver | null = null;
  private autoStarted = false;
  private hotContainer: HTMLElement | null = null;
  private hotTrackingActive = false;
  private readonly hotScrollHandler = () => this.repositionHotLabels();

  /**
   * Called once at app bootstrap in dev. Scans the live DOM, instruments
   * every component it finds, and keeps watching for new ones via a
   * MutationObserver. Hot components get a red overlay on screen — no need
   * to have the inspector open.
   */
  startAutoInstrumentation(): void {
    if (!this.enabled || this.autoStarted) return;
    this.autoStarted = true;
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => this.scanAndInstrument(document.body), 0);
      this.mutationObserver = new MutationObserver(muts => {
        for (const m of muts) {
          for (const node of Array.from(m.addedNodes)) {
            if (node instanceof Element) this.scanAndInstrument(node);
          }
        }
      });
      this.mutationObserver.observe(document.body, { childList: true, subtree: true });
    });
  }

  /**
   * Patch the class's template fn (idempotent per class).
   *
   * Angular caches the template in `def.tView.template` at first render, so
   * patching only `def.template` doesn't affect already-rendered instances.
   * We patch both: `def.template` (for future TViews) and `def.tView.template`
   * (for the live one).
   */
  instrument(instance: unknown): void {
    if (!this.enabled || !instance) return;
    const def = (instance as any).constructor?.ɵcmp;
    if (!def || def.__renderTrackerPatched) return;

    const tracker = this;
    const wrap = (orig: Function) =>
      function (this: unknown, rf: number, ctx: unknown) {
        if (rf !== 2 || !ctx || typeof ctx !== 'object') {
          return orig.call(this, rf, ctx);
        }
        const start = performance.now();
        try {
          return orig.call(this, rf, ctx);
        } finally {
          tracker.record(ctx, performance.now() - start);
        }
      };

    if (typeof def.template === 'function') {
      def.template = wrap(def.template);
    }
    const tView = def.tView;
    if (tView && typeof tView.template === 'function') {
      tView.template = wrap(tView.template);
    }
    def.__renderTrackerPatched = true;
  }

  /** Updates per second in the trailing WINDOW_MS for this instance. */
  updateRate(instance: unknown): number {
    const s = this.trimAndGet(instance);
    if (!s) return 0;
    return Math.round(s.timestamps.length * (1000 / WINDOW_MS));
  }

  /** Total ms of CPU spent inside the template in the last WINDOW_MS. */
  cpuPerSecond(instance: unknown): number {
    const s = this.trimAndGet(instance);
    if (!s) return 0;
    let sum = 0;
    for (const d of s.durations) sum += d;
    return sum * (1000 / WINDOW_MS);
  }

  /** Average ms per render in the trailing WINDOW_MS. */
  avgPerUpdate(instance: unknown): number {
    const s = this.trimAndGet(instance);
    if (!s || s.durations.length === 0) return 0;
    let sum = 0;
    for (const d of s.durations) sum += d;
    return sum / s.durations.length;
  }

  isHot(instance: unknown): boolean {
    const s = this.trimAndGet(instance);
    if (!s || s.durations.length === 0) return false;
    const rate = s.timestamps.length * (1000 / WINDOW_MS);
    if (rate < MIN_UPDATES_PER_SEC) return false;
    let sum = 0;
    for (const d of s.durations) sum += d;
    const avg = sum / s.durations.length;
    const baseline = Math.max(this.baselineMedian(), MIN_BASELINE_MS);
    return avg > baseline * HOT_FACTOR;
  }

  /** Start the periodic tick that forces the inspector UI to re-read rates. */
  startUiRefresh(): void {
    if (!this.enabled || this.refreshTimer) return;
    this.ngZone.runOutsideAngular(() => {
      this.refreshTimer = setInterval(() => {
        this.ngZone.run(() => this._tick.update(v => v + 1));
      }, UI_REFRESH_MS);
    });
  }

  stopUiRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // ── Internals ────────────────────────────────────────────────────────────

  private trimAndGet(instance: unknown): InstanceStats | null {
    if (!this.enabled || !instance || typeof instance !== 'object') return null;
    const s = this.stats.get(instance as object);
    if (!s || s.timestamps.length === 0) return null;
    const cutoff = performance.now() - WINDOW_MS;
    while (s.timestamps.length && s.timestamps[0] < cutoff) {
      s.timestamps.shift();
      s.durations.shift();
    }
    return s;
  }

  private record(instance: object, duration: number): void {
    let s = this.stats.get(instance);
    if (!s) {
      s = { timestamps: [], durations: [] };
      this.stats.set(instance, s);
    }
    const now = performance.now();
    s.timestamps.push(now);
    s.durations.push(duration);

    // Feed the global baseline. Sample the instance's current duration rather
    // than its running avg — cheaper and equally representative over 200 samples.
    this.recentAvgs.push(duration);
    if (this.recentAvgs.length > BASELINE_SAMPLES) this.recentAvgs.shift();

    const last = this.lastHotCheck.get(instance) ?? 0;
    if (now - last > HOT_DEBOUNCE_MS) {
      this.lastHotCheck.set(instance, now);
      this.applyHotIndicator(instance);
    }
  }

  /**
   * 25th-percentile of recent render durations. More robust than the median
   * when the culprit itself dominates the sample buffer: the cheap renders
   * still live in the lower quartile and keep the reference meaningful.
   */
  private baselineMedian(): number {
    const len = this.recentAvgs.length;
    if (len === 0) return 0;
    const sorted = [...this.recentAvgs].sort((a, b) => a - b);
    return sorted[Math.floor(len / 4)];
  }

  private applyHotIndicator(instance: object): void {
    if (!this.isHot(instance)) return;
    if (this.hotOverlays.has(instance)) {
      this.updateHotLabel(instance);
      return;
    }
    const el = this.hostElement(instance);
    if (!el) return;
    const overlay = this.createHotOverlay(el, this.labelText(instance));
    this.hotOverlays.set(instance, overlay);
    this.hotRefs.add(new WeakRef(instance));
    this.ensureHotTracking();
    this.ensureSweeper();
  }

  private labelText(instance: object): string {
    const name = (instance as any).constructor?.name ?? 'Component';
    const ms = this.cpuPerSecond(instance);
    return `⚡ ${name} · ${ms.toFixed(1)}ms/s`;
  }

  private updateHotLabel(instance: object): void {
    const ov = this.hotOverlays.get(instance);
    if (!ov) return;
    ov.label.textContent = this.labelText(instance);
    if (document.body.contains(ov.element)) this.positionLabel(ov.label, ov.element);
  }

  private createHotOverlay(el: HTMLElement, text: string): HotOverlay {
    const origBoxShadow = el.style.boxShadow;
    const origOutline = el.style.outline;
    const origOutlineOffset = el.style.outlineOffset;
    el.style.boxShadow = `inset 0 0 0 1px ${HOT_COLOR}, 0 0 0 1px ${HOT_COLOR}`;
    el.style.outline = `1px solid ${HOT_COLOR}`;
    el.style.outlineOffset = '-1px';

    const container = this.ensureHotContainer();
    const label = document.createElement('div');
    label.style.cssText =
      `position:fixed;font-size:10px;font-family:ui-monospace,monospace;padding:1px 6px;` +
      `border-radius:2px 2px 0 0;white-space:nowrap;pointer-events:none;color:#fff;font-weight:600;` +
      `letter-spacing:0.02em;background:${HOT_LABEL_BG};z-index:99999;line-height:14px;`;
    label.textContent = text;
    container.appendChild(label);
    this.positionLabel(label, el);
    return { element: el, origBoxShadow, origOutline, origOutlineOffset, label };
  }

  private removeHotOverlay(instance: object): void {
    const ov = this.hotOverlays.get(instance);
    if (!ov) return;
    if (document.body.contains(ov.element)) {
      ov.element.style.boxShadow = ov.origBoxShadow;
      ov.element.style.outline = ov.origOutline;
      ov.element.style.outlineOffset = ov.origOutlineOffset;
    }
    ov.label.remove();
    this.hotOverlays.delete(instance);
  }

  private ensureSweeper(): void {
    if (this.sweepTimer) return;
    this.ngZone.runOutsideAngular(() => {
      this.sweepTimer = setInterval(() => this.sweepHot(), SWEEP_MS);
    });
  }

  private sweepHot(): void {
    for (const ref of Array.from(this.hotRefs)) {
      const inst = ref.deref();
      if (!inst) { this.hotRefs.delete(ref); continue; }
      const overlay = this.hotOverlays.get(inst);
      if (!overlay) { this.hotRefs.delete(ref); continue; }
      if (!document.body.contains(overlay.element) || !this.isHot(inst)) {
        this.removeHotOverlay(inst);
        this.hotRefs.delete(ref);
      } else {
        this.updateHotLabel(inst);
      }
    }
    if (this.hotRefs.size === 0) {
      if (this.sweepTimer) { clearInterval(this.sweepTimer); this.sweepTimer = null; }
      this.destroyHotContainer();
      this.stopHotTracking();
    }
  }

  private ensureHotContainer(): HTMLElement {
    if (this.hotContainer) return this.hotContainer;
    const c = document.createElement('div');
    c.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;pointer-events:none;z-index:99999;' +
      'inset:0;margin:0;padding:0;border:none;background:transparent;overflow:visible;';
    c.setAttribute('data-render-tracker-overlays', '');
    c.setAttribute('popover', 'manual');
    document.body.appendChild(c);
    try { (c as any).showPopover?.(); } catch { /* popover unsupported */ }
    this.hotContainer = c;
    return c;
  }

  private destroyHotContainer(): void {
    if (!this.hotContainer) return;
    try { (this.hotContainer as any).hidePopover?.(); } catch { /* */ }
    this.hotContainer.remove();
    this.hotContainer = null;
  }

  private ensureHotTracking(): void {
    if (this.hotTrackingActive) return;
    this.hotTrackingActive = true;
    window.addEventListener('scroll', this.hotScrollHandler, true);
    window.addEventListener('resize', this.hotScrollHandler);
  }

  private stopHotTracking(): void {
    if (!this.hotTrackingActive) return;
    this.hotTrackingActive = false;
    window.removeEventListener('scroll', this.hotScrollHandler, true);
    window.removeEventListener('resize', this.hotScrollHandler);
  }

  private repositionHotLabels(): void {
    for (const ref of this.hotRefs) {
      const inst = ref.deref();
      if (!inst) continue;
      const ov = this.hotOverlays.get(inst);
      if (ov && document.body.contains(ov.element)) this.positionLabel(ov.label, ov.element);
    }
  }

  private positionLabel(label: HTMLElement, target: HTMLElement): void {
    const rect = target.getBoundingClientRect();
    label.style.left = `${rect.left}px`;
    label.style.top = `${Math.max(0, rect.top - 16)}px`;
  }

  private hostElement(instance: object): HTMLElement | null {
    const ng = (window as any).ng;
    if (!ng?.getHostElement) return null;
    try { return ng.getHostElement(instance) as HTMLElement; }
    catch { return null; }
  }

  private scanAndInstrument(root: Element): void {
    const ng = (window as any).ng;
    if (!ng?.getComponent) return;
    const walk = (el: Element) => {
      try {
        const cmp = ng.getComponent(el);
        if (cmp) this.instrument(cmp);
      } catch { /* ignore */ }
      const children = el.children;
      for (let i = 0; i < children.length; i++) walk(children[i]);
    };
    walk(root);
  }
}

/**
 * Provider that auto-starts the render-tracker at bootstrap. Add to the
 * root providers in `main.ts` so hot components get highlighted on screen
 * even when the inspector isn't open. No-op in production builds.
 */
export function provideRenderTracker(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => inject(RenderTrackerService).startAutoInstrumentation(),
    },
  ]);
}